import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Data, useDataQuery } from '../../hooks/use-data2.hook';
import { useHandleClick } from '../../hooks/use-handle-click';
import {
    ApiConfig,
    DataPackage,
    Indicator,
} from '../../types';
import { ColorLegend } from '../ColorLegend';
import { TimeLine } from '../TimeLine';
import { ViewPicker } from '../ViewPicker';

import { getRouteApi } from '@tanstack/react-router';
import { Feature } from 'geojson';
import { useShallow } from 'zustand/shallow';
import { FEATURES, TRANSITION_DURATION_MS } from '../../constants';
import { getMinimumTimerange } from '../../get-minimumtimerange';
import { featureIsIncludedInCurrentFilter } from '../../helpers/filter';
import { useTitle } from '../../hooks/use-title.hook';
import { usePlayerStore } from '../../player.store';
import { Tooltip } from '../Viz/Tooltip';
import { Menu } from './Menu';
import { Options } from './Options';
import { useToolStore } from './tool.store';
import {
    ClearSelectionButton,
    MapTypeSelector,
    useMeanValuesByYear,
} from '../Viz/helpers';
import { FuzzySearch } from '../FuzzySearch/FuzzySearch';
import { YearBadge } from '../YearBadge/YearBadge';
import { useTimeRangeYears } from '../../hooks/use-timerangeyears';
import { useTranslation } from 'react-i18next';
import { AIButton, AIPanel } from '../AIAnalysis';
import { useAIStore } from '../AIAnalysis/ai-store';
import {
    formatLanName,
    getLanSelectionOutcome,
    getSelectedLanCode,
    packageCoversLan,
} from '../../lan-coverage/lan-coverage';

const routeApi = getRouteApi('/tool/');

const VizScatter = lazy(() => import('../Viz/VizScatter'));
const VizMap = lazy(() =>
    import('../Viz/VizMap').then((module) => ({ default: module.VizMap }))
);
const LanMap = lazy(() => import('../Viz/LanMap'));

export function ToolWrapper(props: {
    config: ApiConfig;
    indicators: Indicator[];
}) {
    const { t } = useTranslation();

    const toolStore = useToolStore(
        useShallow((state) => ({
            indicatorsMap: state.indicatorsMap,
            getDataOptions: state.getDataOptions,
            geoFilterIds: state.geoFilterIds,
        }))
    );

    const dataQuery = useDataQuery(
        toolStore.getDataOptions(),
        toolStore.indicatorsMap,
        toolStore.geoFilterIds
    );

    if (dataQuery === null) {
        return (
            <div className="fixed inset-0 grid place-items-center">
                <div className="flex flex-col gap-1">
                    Oops! {t('missing-indicator-warning')}
                    <a href="/" className="link-secondary">
                        reset
                    </a>
                </div>
            </div>
        );
    }

    if (dataQuery?.error) {
        return (
            <div className="fixed inset-0 grid place-items-center">
                <div className="flex flex-col gap-1">
                    Oops! {dataQuery.error.message}
                    <a href="/" className="link-secondary">
                        reset
                    </a>
                </div>
            </div>
        );
    }

    if (!dataQuery?.data) {
        return null;
    }

    return (
        <Tool
            data={dataQuery.data}
            indicators={props.indicators}
            config={props.config}
        />
    );
}

export function Tool(props: {
    config: ApiConfig;
    indicators: Indicator[];
    data: Data;
}) {
    const { t, i18n } = useTranslation();
    useTitle(import.meta.env.VITE_SITE_NAME);

    const search = routeApi.useSearch();
    const searchRef = useRef(search);
    searchRef.current = search;

    // Temporary feature flag toggle on window object to be able to test functionality before officially exposing feature
    const [shouldShowAiButton, setShouldShowAiButton] = useState(FEATURES.AI_PANEL)
    // @ts-ignore
    window.toggleAiButton = () => {
        setShouldShowAiButton(value => !value)
    }

    const toolStore = useToolStore(
        useShallow((state) => ({
            activeFeatureId: state.activeFeatureId,
            indicatorsMap: state.indicatorsMap,
            setActiveFeatureId: state.setActiveFeatureId,
            setTooltipFeatures: state.setTooltipFeatures,
            shouldShowScatterPaths: state.shouldShowScatterPaths,
            showScatterPaths: state.showScatterPaths,
            hideScatterPaths: state.hideScatterPaths,
            timeRangeMap: state.timeRangeMap,
            tooltipFeatures: state.tooltipFeatures,
            setScaleType: state.setScaleType,
            colorScheme: state.colorScheme,
            colorType: state.colorType,
            scaleType: state.scaleType,
            geoLevel: state.geoLevel,
            mapGeoLevel: state.mapGeoLevel,
            geoFilterIds: state.geoFilterIds,
            setGeoFilterIds: state.setGeoFilterIds,
            lanName: state.lanName,
            setLanName: state.setLanName,
            setMapGeoLevel: state.setMapGeoLevel,
            selectedFeatures: state.selectedFeatures,
            clearSelectedFeatures: state.clearSelectedFeatures,
            featureFilter: state.featureFilter,
            setFeatureFilter: state.setFeatureFilter,
            shouldZoomToFiltered: state.shouldZoomToFiltered,
            mapType: state.mapType,
            view: state.view,
            setView: state.setView,
            setGeoLevel: state.setGeoLevel,
            animationBuster: state.animationBuster,
            getDataOptions: state.getDataOptions,
            indicators: state.indicators,
            setIndicators: state.setIndicators,
            toggleFeatureSelection: state.toggleFeatureSelection,
        }))
    );

    const aiStoreReset = useAIStore((state) => state.reset);
    const shouldShowAIPanel = useAIStore((state) => state.shouldShowAIPanel);

    const playerStore = usePlayerStore(
        useShallow((state) => ({
            intValue: state.intValue,
            initialize: state.initialize,
            state: state.state,
            animationBuster: state.animationBuster,
            pause: state.pause,
        }))
    );

    useEffect(() => {
        aiStoreReset();
    }, [
        toolStore.geoLevel,
        toolStore.indicators?.x,
        toolStore.indicators?.y,
        toolStore.indicators?.r,
        toolStore.featureFilter,
    ]);

    // The year from the URL (?year=) applies to the data the tool was opened
    // with. Comparing against the initial data (rather than a "has applied"
    // flag) keeps this correct when effects run twice in StrictMode.
    const initialUrlYear = useRef({
        data: props.data,
        // Hydrated from the query string, so it may be a string
        year: Number(useToolStore.getState().year),
    });

    useEffect(() => {
        const timeRange = getMinimumTimerange({
            geoLevel: props.data.options.geoLevel,
            indicators: Object.values(props.data.options.indicators).filter(
                (f) => f
            ) as Indicator[],
            timeRangeMap: toolStore.timeRangeMap,
        });

        const { data: initialData, year: urlYear } = initialUrlYear.current;
        const startValue =
            props.data === initialData &&
            urlYear >= timeRange[0] &&
            urlYear <= timeRange[1]
                ? urlYear
                : timeRange[0];

        playerStore.initialize({
            min: timeRange[0],
            max: timeRange[1],
            duration: TRANSITION_DURATION_MS * (timeRange[1] - timeRange[0]),
            startValue,
        });
        // Keep the URL in line with the year actually shown (e.g. when the
        // URL year was outside the time range)
        useToolStore.getState().setYear(Math.ceil(startValue));
    }, [props.data]);

    // Mirror the current year into the tool store so it persists to the URL.
    // Subscribing (rather than reading intValue from render) avoids writing a
    // stale year in the same commit as the initialization above.
    useEffect(
        () =>
            usePlayerStore.subscribe((state, prev) => {
                if (state.intValue !== prev.intValue) {
                    useToolStore.getState().setYear(state.intValue);
                }
            }),
        []
    );

    const kommunQuery = useDataQuery(
        {
            ...toolStore.getDataOptions(),
            geoLevel: 'kommun',
        },
        toolStore.indicatorsMap,
        toolStore.geoFilterIds
    );

    const kommunPoints = kommunQuery?.data?.points || [];

    useHandleClick();

    useEffect(() => {
        function handleKeyDown(event: KeyboardEvent) {
            // Don't handle keyboard shortcuts when user is typing in an input
            const target = event.target as HTMLElement;
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            ) {
                return;
            }

            if (event.key === 'l' && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                toolStore.shouldShowScatterPaths
                    ? toolStore.hideScatterPaths()
                    : toolStore.showScatterPaths();
            }
        }

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    });

    const timeRange = getMinimumTimerange({
        geoLevel: props.data.options.geoLevel,
        indicators: Object.values(props.data.options.indicators).filter(
            (f) => f
        ) as Indicator[],
        timeRangeMap: toolStore.timeRangeMap,
    });

    const lanCode = getSelectedLanCode(toolStore.geoFilterIds);
    const lanDisplayName = toolStore.lanName
        ? formatLanName(toolStore.lanName)
        : lanCode;

    // Only offer packages that have data in the selected län.
    const dataPackages = props.config.dataPackages.filter((dataPackage) =>
        packageCoversLan(
            dataPackage,
            toolStore.timeRangeMap,
            dataPackage.forceGeolevel ?? toolStore.geoLevel,
            lanCode
        )
    );

    const currentIndicatorsCoverLan =
        !toolStore.indicators ||
        packageCoversLan(
            toolStore.indicators,
            toolStore.timeRangeMap,
            toolStore.geoLevel,
            lanCode
        );

    const selectedDataPackage =
        dataPackages.find((datapackage) => {
            return (
                toolStore.indicators?.x === datapackage?.x &&
                toolStore.indicators?.y === datapackage?.y &&
                toolStore.indicators?.r === datapackage?.r &&
                toolStore.indicators?.c === datapackage?.c
            );
        }) ?? null;

    const getPackageName = (dataPackage: DataPackage) =>
        dataPackage.name[i18n.language] ?? dataPackage.name['sv'];

    const getLanOutcome = (geoId: string) =>
        getLanSelectionOutcome({
            currentIndicators: toolStore.indicators,
            dataPackages: props.config.dataPackages,
            timeRangeMap: toolStore.timeRangeMap,
            geoLevel: 'kommun',
            lanCode: geoId,
        });

    function handleSetSelectedDatapackage(newPackage: DataPackage) {
        const newPackageMissingIndicators =
            !newPackage.x || !newPackage.y || !newPackage.r;
        const indicatorsMapMissingIndicators = [
            newPackage.x,
            newPackage.y,
            newPackage.r,
        ].some((d) => !toolStore.indicatorsMap[d]);

        if (newPackageMissingIndicators || indicatorsMapMissingIndicators) {
            const missingInPackage = (["x", "y", "r"] as const)
                .filter((key) => !newPackage[key])
                .map((key) => `  ${key}: ${JSON.stringify(newPackage[key])}`);
            const missingInMap = (["x", "y", "r"] as const)
                .filter((key) => newPackage[key] && !toolStore.indicatorsMap[newPackage[key]])
                .map((key) => `  ${key}: "${newPackage[key]}"`);
            console.error(
                `Missing indicators in newPackage "${newPackage.id}":\n` +
                (missingInPackage.length
                    ? `  Missing values in package:\n${missingInPackage.join("\n")}\n`
                    : "") +
                (missingInMap.length
                    ? `  Not found in indicatorsMap:\n${missingInMap.join("\n")}\n`
                    : "")
            );
            return;
        }

        playerStore.pause();

        toolStore.setIndicators({
            x: newPackage.x,
            y: newPackage.y,
            r: newPackage.r,
            c: newPackage.c,
        });

        if (newPackage.forceGeolevel) {
            const allowed = props.config.geoLevels.some(
                (g) => g.id === newPackage.forceGeolevel
            );
            if (allowed && toolStore.geoLevel !== newPackage.forceGeolevel) {
                toolStore.setGeoLevel(newPackage.forceGeolevel);
            }
        } else if (toolStore.geoLevel === 'nyko6') {
            // If we were on nyko6 (forced by previous package), and the new package
            // doesn't force it, fallback to a sensible default (prefer regso)
            const preferred = props.config.geoLevels.find(
                (g) => g.id === 'regso'
            );
            const firstNonNyko6 = props.config.geoLevels.find(
                (g) => g.id !== 'nyko6'
            );
            const fallback = preferred ?? firstNonNyko6;
            if (fallback) {
                toolStore.setGeoLevel(fallback.id as any);
            }
        }
    }

    // Data now includes points array for compatibility
    const points = props.data.points;
    const polygons = props.data.polygons;
    const options = props.data.options;

    const years = useTimeRangeYears({
        dataOptions: toolStore.getDataOptions(),
        timeRangeMap: toolStore.timeRangeMap,
    });

    const meanByYear = useMeanValuesByYear({
        dataFeatures: points,
        featureFilter: toolStore.featureFilter,
        years,
        ...props.data.options.indicators,
    });

    const handleTooltip = ({ object }: { object: Feature }) => {
        if (!object) return null;
        if (
            !featureIsIncludedInCurrentFilter(object, toolStore.featureFilter)
        ) {
            return null;
        }

        const kommunPoint = kommunPoints.find((kommun) => {
            const kommunGeoId = kommun.properties?.geoId;
            return kommunGeoId && object.properties?.geoId?.startsWith(kommunGeoId);
        });
        return { hovered: object, kommun: kommunPoint };
    };

    const handleHover = (id: string | null) => {
        const currentId = toolStore.activeFeatureId;
        if (id !== currentId) {
            toolStore.setActiveFeatureId(id);
            const object = points.find((d) => d.properties?.id === id);
            if (object) {
                const tooltipFeatures = handleTooltip({ object });
                if (tooltipFeatures) {
                    toolStore.setTooltipFeatures(tooltipFeatures);
                }
            }
        }
    };

    const scaleType = options.indicators.cVar?.scaleType ?? toolStore.scaleType;
    const colorType = options.indicators.cVar?.colorType ?? toolStore.colorType;

    const shouldShowMap =
        toolStore.view === 'map' || toolStore.view === 'bubble_and_map';
    const shouldShowScatter =
        toolStore.view === 'bubble' || toolStore.view === 'bubble_and_map';

    return (
        <div className="h-full p-3 bg-base-300 flex flex-col gap-3 overflow-x-hidden">
            {toolStore.mapGeoLevel === 'lan' && (
                <Suspense fallback={null}>
                    <LanMap
                        onSelectLan={({ geoId, name }) => {
                            toolStore.setGeoFilterIds([geoId]);
                            toolStore.setLanName(name);
                            toolStore.setFeatureFilter([]);
                            toolStore.setMapGeoLevel('kommun');

                            // Switch to the first package with data in the new län
                            // if the current indicators lack it. LanMap explains
                            // this before the click.
                            const outcome = getLanOutcome(geoId);
                            if (outcome.type === 'switch') {
                                handleSetSelectedDatapackage(outcome.dataPackage);
                            }
                        }}
                        onClose={() => toolStore.setMapGeoLevel(toolStore.geoLevel)}
                        getOutcome={getLanOutcome}
                        getPackageName={getPackageName}
                    />
                </Suspense>
            )}
            <div className="flex flex-row gap-3 flex-1 h-full min-h-0 relative">
                <Menu
                    indicators={props.indicators ?? []}
                    config={props.config}
                    dataPackages={dataPackages}
                    selectedDataPackage={selectedDataPackage}
                    timeRange={timeRange}
                    dataOptions={props.data.options}
                    data={props.data}
                    onSetSelectedDatapackage={handleSetSelectedDatapackage}
                    onClearSelectedFeatures={toolStore.clearSelectedFeatures}
                    points={points}
                />

                <div className="grid grid-cols-1 grid-rows-[1fr_auto] flex-1 gap-3">
                    {/* Main visualization */}
                    <div className="flex flex-row gap-3 min-h-0 overflow-hidden">
                        <div
                            className={` h-full flex-1 rounded-lg grid grid-cols-2 grid-rows-1 relative overflow-hidden`}
                        >
                            {toolStore.activeFeatureId &&
                                toolStore.tooltipFeatures && (
                                    <div
                                        className={`absolute z-30 bottom-2 pointer-events-none ${
                                            shouldShowMap ? 'right-12' : 'right-1'
                                        }`}
                                    >
                                        <Tooltip
                                            tooltipFeatures={
                                                toolStore.tooltipFeatures
                                            }
                                            geoLevel={props.data.options.geoLevel}
                                            dataOptions={props.data.options}
                                            year={playerStore.intValue}
                                            meanByYear={meanByYear}
                                            years={years}
                                        />
                                    </div>
                                )}
                            {!currentIndicatorsCoverLan && (
                                <div className="absolute left-1/2 -translate-x-1/2 top-4 z-20 max-w-md w-[calc(100%-2rem)]">
                                    <div role="alert" className="alert alert-warning">
                                        {t('ingen-data-for-lan', {
                                            lan: lanDisplayName,
                                            defaultValue: `Valda indikatorer saknar data för ${lanDisplayName}.`,
                                        })}
                                    </div>
                                </div>
                            )}
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-16 z-10 pointer-events-none">
                                <YearBadge year={playerStore.intValue} />
                            </div>
                            <Suspense fallback={null}>
                                {shouldShowScatter && (
                                    <div
                                        className="relative h-full bg-white"
                                        style={{
                                            gridColumn:
                                                toolStore.view === 'bubble'
                                                    ? 'span 2'
                                                    : 'span 1',
                                        }}
                                    >
                                        <VizScatter
                                            key={toolStore.view}
                                            dataOptions={options}
                                            dataFeatures={points}
                                            year={playerStore.intValue}
                                            selectedFeatures={
                                                toolStore.selectedFeatures
                                            }
                                            onClearSelection={
                                                toolStore.clearSelectedFeatures
                                            }
                                            animationBuster={Math.max(
                                                playerStore.animationBuster,
                                                toolStore.animationBuster
                                            )}
                                            featureFilter={
                                                toolStore.featureFilter
                                            }
                                            setActiveFeatureId={handleHover}
                                        />
                                    </div>
                                )}
                        {shouldShowMap && (
                                    <div
                                        className="relative h-full bg-white"
                                        style={{
                                            gridColumn:
                                                toolStore.view === 'map'
                                                    ? 'span 2'
                                                    : 'span 1',
                                        }}
                                    >
                                        <VizMap
                                            data={props.data}
                                            key={toolStore.view}
                                            dataOptions={options}
                                            dataFeatures={points}
                                            polygons={polygons}
                                            year={playerStore.intValue}
                                            selectedFeatures={
                                                toolStore.selectedFeatures
                                            }
                                            onClearSelection={
                                                toolStore.clearSelectedFeatures
                                            }
                                            animationBuster={Math.max(
                                                playerStore.animationBuster,
                                                toolStore.animationBuster
                                            )}
                                            featureFilter={
                                                toolStore.featureFilter
                                            }
                                            zoomToFiltered={
                                                toolStore.shouldZoomToFiltered
                                            }
                                            mapType={toolStore.mapType}
                                            colorScale={toolStore.colorScheme}
                                            colorType={colorType}
                                            scaleType={scaleType}
                                            setActiveFeatureId={handleHover}
                                            geoFilterIds={toolStore.geoFilterIds}
                                            controls={<MapTypeSelector />}
                                        />
                                    </div>
                                )}
                            </Suspense>
                        </div>
                        {shouldShowAIPanel && (
                            <div className="max-w-lg w-full flex-none h-full overflow-y-auto">
                                <AIPanel />
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <FuzzySearch
                            data={points.filter((point) =>
                                featureIsIncludedInCurrentFilter(
                                    point,
                                    toolStore.featureFilter
                                )
                            )}
                            onSelect={(id) => {
                                console.log(id);
                                toolStore.toggleFeatureSelection(id);
                            }}
                            searchKeys={[
                                'properties.id',
                                'properties.kommunnamn',
                                'properties.name',
                            ]}
                            displayProperty="name"
                            selectedFeatures={toolStore.selectedFeatures}
                            maxResults={10}
                        />
                        {toolStore.selectedFeatures.length > 0 && (
                            <ClearSelectionButton
                                onClick={toolStore.clearSelectedFeatures}
                            />
                        )}
                        <div className="flex-1 flex justify-center items-center gap-2">
                            <ViewPicker
                                view={toolStore.view}
                                onSetView={toolStore.setView}
                            />
                            {props.data.hasInterpolatedValues && (
                                <div
                                    className="tooltip"
                                    data-tip={t('interpolated-values-warning')}
                                    tabIndex={0}
                                    aria-label={t('interpolated-values-warning') as string}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 text-warning"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-4 items-center">
                            {shouldShowAiButton && <AIButton />}
                            <Options />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex gap-3">
                <div className="bg-base-100 rounded-lg flex-1">
                    <TimeLine key={timeRange.join('-')} timeRange={timeRange} />
                </div>
                {toolStore.indicators?.c && (
                    <div className="bg-base-100 rounded-lg p-3">
                        <ColorLegend
                            markValue={
                                toolStore.tooltipFeatures?.hovered?.properties
                                    ?.values[
                                    options.indicators.cVar?.id ?? ''
                                ]?.[playerStore.intValue]
                            }
                            showHeader={false}
                            cVar={options.indicators.cVar}
                            data={points}
                            featureFilter={toolStore.featureFilter}
                            zoomToFiltered={toolStore.shouldZoomToFiltered}
                            colorScheme={toolStore.colorScheme}
                            colorType={colorType}
                            scaleType={scaleType}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
